import React, { useEffect, useState, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Button, Dropdown, Nav, Spinner, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken, getUserId } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from '../contexts/LoadingContext';

const DESIRABLE_LABELS = [
  'รักชาติ ศาสน์ กษัตริย์',
  'ซื่อสัตย์สุจริต',
  'มีวินัย',
  'ใฝ่เรียนรู้',
  'อยู่อย่างพอเพียง',
  'มุ่งมั่นในการทำงาน',
  'รักความเป็นไทย',
  'มีจิตสาธารณะ',
];

const SCORE_COLORS = {
  null:  '#f8f9fa',
  '':    '#f8f9fa',
  '0':   '#f8d7da',
  '1':   '#fff3cd',
  '2':   '#cfe2ff',
  '3':   '#d1e7dd',
};

const SCORE_LABELS = { '0': 'ไม่ผ่าน', '1': 'ผ่าน', '2': 'ดี', '3': 'ดีเยี่ยม' };

function ScoreCell({ value, onChange }) {
  return (
    <div className="d-flex gap-1 justify-content-center">
      {['0', '1', '2', '3'].map(v => {
        const isSelected = value === v;
        const isDanger = v === '0';
        return (
          <button
            key={v}
            onClick={() => onChange(isSelected ? null : v)}
            style={{
              width: 28, height: 26,
              border: `1px solid ${isDanger ? '#dc3545' : '#999'}`,
              borderRadius: 4, cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
              background: isSelected ? SCORE_COLORS[v] : '#fff',
              color: isDanger ? '#dc3545' : 'inherit',
              outline: isSelected ? `2px solid ${isDanger ? '#dc3545' : '#0d6efd'}` : 'none',
              transition: 'all 0.1s'
            }}
            title={SCORE_LABELS[v]}
          >
            {v}
          </button>
        );
      })}
    </div>
  );
}

export default function DesirableEntry() {
  const { setLoading } = useLoading();
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    const tables = document.getElementsByTagName('table');
    for (const table of tables) {
      const theads = table.querySelectorAll('thead');
      if (skin === 'dark') {
        for (const thead of theads) { thead.classList.remove('table-secondary'); thead.classList.add('table-primary'); }
        table.classList.add('table-dark');
      } else {
        table.classList.remove('table-dark');
        for (const thead of theads) { thead.classList.remove('table-primary'); thead.classList.add('table-secondary'); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const [yearList, setYearList]       = useState([]);
  const [yearId, setYearId]           = useState(() => localStorage.getItem('yearId') || null);
  const [selectedYear, setSelectedYear] = useState('');
  const [semester, setSemester]       = useState('1');
  const [data, setData]               = useState([]);
  const [dirty, setDirty]             = useState(false);
  const [saving, setSaving]           = useState(false);

  // load year list
  useEffect(() => {
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
    .then(res => {
      setYearList(res.data);
      const stored = localStorage.getItem('yearId');
      const id = stored || (res.data[0]?.year_id ?? null);
      setYearId(id);
      const found = res.data.find(y => String(y.year_id) === String(id));
      if (found) setSelectedYear(`ปีการศึกษา ${found.year_year}`);
    })
    .catch(() => {});
  }, []);

  // load students + scores whenever year/semester changes
  const loadData = useCallback(() => {
    if (!yearId) return;
    setLoading(true);
    axiosInstance.get(
      `${process.env.REACT_APP_API}/getDesirableByAdvisor/${getUserId()}/${yearId}/${semester}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    )
    .then(res => {
      setData(res.data.map(r => ({
        study_id:    r.study_id,
        st_idnumber: r.st_idnumber,
        st_name:     r.st_name,
        st_status:   r.st_status,
        dr_item1:    r.dr_item1 != null ? String(r.dr_item1) : null,
        dr_item2:    r.dr_item2 != null ? String(r.dr_item2) : null,
        dr_item3:    r.dr_item3 != null ? String(r.dr_item3) : null,
        dr_item4:    r.dr_item4 != null ? String(r.dr_item4) : null,
        dr_item5:    r.dr_item5 != null ? String(r.dr_item5) : null,
        dr_item6:    r.dr_item6 != null ? String(r.dr_item6) : null,
        dr_item7:    r.dr_item7 != null ? String(r.dr_item7) : null,
        dr_item8:    r.dr_item8 != null ? String(r.dr_item8) : null,
      })));
      setDirty(false);
    })
    .catch(err => {
      if (err.response?.status !== 404) {
        Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || err.message });
      }
      setData([]);
    })
    .finally(() => setLoading(false));
  }, [yearId, semester, setLoading]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleScoreChange = (index, field, value) => {
    setData(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setDirty(true);
  };

  const handleSave = () => {
    setSaving(true);
    axiosInstance.put(`${process.env.REACT_APP_API}/saveDesirable`, {
      semester,
      update_by: String(getUserId()),
      students: data.map(s => ({
        study_id: s.study_id,
        dr_item1: s.dr_item1 !== null ? Number(s.dr_item1) : null,
        dr_item2: s.dr_item2 !== null ? Number(s.dr_item2) : null,
        dr_item3: s.dr_item3 !== null ? Number(s.dr_item3) : null,
        dr_item4: s.dr_item4 !== null ? Number(s.dr_item4) : null,
        dr_item5: s.dr_item5 !== null ? Number(s.dr_item5) : null,
        dr_item6: s.dr_item6 !== null ? Number(s.dr_item6) : null,
        dr_item7: s.dr_item7 !== null ? Number(s.dr_item7) : null,
        dr_item8: s.dr_item8 !== null ? Number(s.dr_item8) : null,
      }))
    }, { headers: { Authorization: `Bearer ${getToken()}` } })
    .then(() => {
      setDirty(false);
      Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1200, showConfirmButton: false });
    })
    .catch(err => {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || err.message });
    })
    .finally(() => setSaving(false));
  };

  const handleSelectYear = (id) => {
    const found = yearList.find(y => y.year_id === id);
    if (found) {
      setYearId(id);
      setSelectedYear(`ปีการศึกษา ${found.year_year}`);
      localStorage.setItem('yearId', id);
    }
  };

  // summary counts
  const countFilled = data.filter(s =>
    [s.dr_item1,s.dr_item2,s.dr_item3,s.dr_item4,s.dr_item5,s.dr_item6,s.dr_item7,s.dr_item8].every(v => v !== null)
  ).length;

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ที่ปรึกษาห้องเรียน</Link></li>
              <li className="breadcrumb-item active">คุณลักษณะอันพึงประสงค์</li>
            </ol>
            <h4 className="main-title mb-0">คุณลักษณะอันพึงประสงค์</h4>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-year">
                {selectedYear || 'เลือกปีการศึกษา'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {yearList.map(y => (
                  <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                    ปีการศึกษา {y.year_year}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <Row className="g-12 mb-4">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <Nav variant="tabs" activeKey={semester} onSelect={k => { if (dirty) { if (!window.confirm('มีข้อมูลที่ยังไม่ได้บันทึก ต้องการเปลี่ยนภาคเรียนหรือไม่?')) return; } setSemester(k); }}>
                    <Nav.Item><Nav.Link eventKey="1">ภาคเรียนที่ 1</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="2">ภาคเรียนที่ 2</Nav.Link></Nav.Item>
                  </Nav>
                  <small className="text-muted">
                    ประเมินแล้ว <Badge bg="success">{countFilled}</Badge> / {data.length} คน
                  </small>
                </div>
                <Button
                  variant={dirty ? 'warning' : 'success'}
                  size="sm"
                  onClick={handleSave}
                  disabled={saving || data.length === 0}
                >
                  {saving
                    ? <Spinner animation="border" size="sm" />
                    : <><i className="ri-save-3-fill me-1"></i>บันทึกทั้งหมด</>}
                </Button>
              </Card.Header>

              <Card.Body className="p-2" style={{ overflowX: 'auto' }}>
                {/* คำอธิบายคะแนน */}
                <div className="d-flex gap-3 mb-2 ms-1" style={{ fontSize: '12px' }}>
                  {Object.entries(SCORE_LABELS).map(([k, v]) => (
                    <span key={k}>
                      <span style={{ display: 'inline-block', width: 18, height: 16, background: SCORE_COLORS[k], border: '1px solid #999', borderRadius: 3, verticalAlign: 'middle', marginRight: 3 }} />
                      {k} = {v}
                    </span>
                  ))}
                  <span className="text-muted">( คลิกซ้ำที่ปุ่มเดิมเพื่อยกเลิก )</span>
                </div>

                <Table responsive bordered hover size="sm" className="mb-0 align-middle" style={{ minWidth: 1100 }}>
                  <thead>
                    <tr className="text-center align-middle">
                      <th style={{ width: 40 }}>ที่</th>
                      <th style={{ width: 90 }}>เลขประจำตัว</th>
                      <th style={{ minWidth: 160, textAlign: 'left' }}>ชื่อ-สกุล</th>
                      {DESIRABLE_LABELS.map((label, i) => (
                        <th key={i} style={{ width: 90, fontSize: '11px', lineHeight: 1.2 }}>
                          {i + 1}. {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.length > 0 ? data.map((student, index) => {
                      const allFilled = ['dr_item1','dr_item2','dr_item3','dr_item4','dr_item5','dr_item6','dr_item7','dr_item8']
                        .every(k => student[k] !== null);
                      return (
                        <tr key={student.study_id} className={student.st_status === '0' ? 'table-danger' : allFilled ? 'table-success' : ''}>
                          <td className="text-center">{index + 1}</td>
                          <td className="text-center">{student.st_idnumber}</td>
                          <td>{student.st_name}</td>
                          {['dr_item1','dr_item2','dr_item3','dr_item4','dr_item5','dr_item6','dr_item7','dr_item8'].map(field => (
                            <td key={field} className="text-center p-1" style={{ background: SCORE_COLORS[student[field]] }}>
                              <ScoreCell
                                value={student[field]}
                                onChange={v => handleScoreChange(index, field, v)}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    }) : (
                      <tr><td colSpan={11} className="text-center text-muted py-4">ไม่พบข้อมูลนักเรียน</td></tr>
                    )}
                  </tbody>
                </Table>

                {dirty && (
                  <div className="d-flex justify-content-end mt-2">
                    <Button variant="warning" size="sm" onClick={handleSave} disabled={saving}>
                      {saving ? <Spinner animation="border" size="sm" /> : <><i className="ri-save-3-fill me-1"></i>บันทึกทั้งหมด</>}
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </React.Fragment>
  );
}
